import React, { DetailedHTMLProps, HTMLAttributes } from 'react';

import { ProcessSelection } from '../process-selection/process-selection';
import { ButtonActionState, ButtonIcon } from '../button-icon/button-icon';
import theme from '../../theme';
import {
  ButtonGroup as ChakraButtonGroup,
  Button as ChakraButton,
  Flex
} from '@chakra-ui/react';
// Toast-less provider: @chakra-ui/react's ChakraProvider also mounts a
// ToastProvider, and every ToastProvider renders the singleton toast queue —
// so nested providers double every toast (CDMX-17400). The base provider gives
// the same theming without a second toast container.
import { ChakraProvider as ChakraThemeProvider } from '@chakra-ui/provider';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import './styles/index.scss';
import * as dataURIs from '../case-detail/_data-uris';

type DashboardActionBarProps = {
  caseRun: CMxAPI.CaseRun;
  actionsState: {
    [key: string]: ButtonActionState;
  };
  handleBackClick: (event: React.MouseEvent) => void;
  handleProcessIdChange: (event: React.MouseEvent) => void;
  handleOpenJson: (event: React.MouseEvent) => void;
  caseRuns: {
    [key: string]: CMxAPI.CaseRun[];
  };
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const DashboardActionBar: React.FC<DashboardActionBarProps> = (
  props: DashboardActionBarProps
) => {
  const {
    actionsState,
    handleBackClick,
    handleProcessIdChange,
    handleOpenJson,
    caseRuns,
    caseRun
  } = props;

  const showAmp =
    actionsState.launchAmplify?.visible || actionsState.importAmplify?.visible;

  return (
    <ChakraThemeProvider theme={theme}>
      <div className="action-bar case-list">
        <div className="nav-bar-columns">
          <div className="nav-bar-column nav-bar-left-column">
            <ChakraButton
              variant="secondary"
              size="sm"
              onClick={handleBackClick}
              className="qa-back-link"
              leftIcon={<BiomeIcon iconName={IconName.Arrow_Left} />}>
              Back to Cases
            </ChakraButton>
          </div>

          <div className="pro-serve-links nav-bar-middle-column no-loading">
            {showAmp && (
              <ChakraButtonGroup size="sm" variant="secondary" isAttached>
                <ButtonIcon
                  isAttached={true}
                  actionsState={actionsState.importAmplify}
                  data-href="/import-amplify/"
                  data-target="_blank"
                  onClick={handleOpenJson}
                  iconName={IconName.Export}
                  label="Export Case to Amplify"
                  className="upload-icon"></ButtonIcon>
                <ButtonIcon
                  actionsState={actionsState.launchAmplify}
                  data-href="/launch-amplify/"
                  data-target="_blank"
                  onClick={handleOpenJson}
                  iconName={IconName.Launch}
                  label="Launch Amplify"
                  className="rocket-icon"></ButtonIcon>
              </ChakraButtonGroup>
            )}
            <ChakraButtonGroup size="sm" variant="secondary" isAttached>
              <ButtonIcon
                isAttached={true}
                actionsState={actionsState.bpmSteps}
                data-href="/bpm-steps/"
                data-target="_blank"
                onClick={handleOpenJson}
                iconName={IconName.Footprints}
                label="BPM Process"
                className="shoe-prints-icon"></ButtonIcon>
              <ButtonIcon
                isAttached={true}
                actionsState={actionsState.jsonView}
                data-href="/json-view/"
                data-target="_blank"
                label="Case JSON"
                iconName={IconName.Source}
                onClick={handleOpenJson}
                className="code-icon"
              />
              <ButtonIcon
                actionsState={actionsState.caseDocuments}
                data-href="/case-documents/"
                data-target="_blank"
                label="Documents"
                iconName={IconName.Notes_Stacked}
                onClick={handleOpenJson}
                className="file-medical-icon"
              />
            </ChakraButtonGroup>
          </div>

          <div className="nav-bar-column nav-bar-right-column">
            <Flex
              className="no-loading"
              align="center"
              justify="flex-end"
              gap={3}
              wrap="wrap">
              <span className="report-controls control-section">
                <span className="report-label">Documentation </span>
              </span>
              <Flex className="buttons btn-group" align="center">
                <ProcessSelection
                  caseRuns={caseRuns}
                  activeId={caseRun.process_instance_id}
                  onClick={handleProcessIdChange}
                />
              </Flex>
            </Flex>
          </div>
        </div>
      </div>
    </ChakraThemeProvider>
  );
};
DashboardActionBar.displayName = 'DashboardActionBar';

export { DashboardActionBar };
